/**
 * <expandable-list> Custom Element
 *
 * Replaces theme/js/modules/expandable-lists.js (IIFE module).
 * Progressive enhancement — upgrades on connection, no central init() call.
 *
 * Contract:
 *   - Adds 'list-init' class after upgrade
 *   - Toggles 'expanded' class on open/close
 *   - Dispatches native CustomEvents 'list:open' / 'list:close' on the
 *     element (bubbling, with detail = true|false to mirror the legacy
 *     jQuery trigger signature). New listeners use addEventListener.
 *   - Closes open sibling lists when opening a new one
 *   - Closes open child lists when the parent closes
 *   - Element instances expose .open() / .close() methods for
 *     programmatic control (replaces the retired $.fn.expandableList plugin).
 *
 * AI: Entirely written by AI
 * Date: 2026-04-24
 * Model: Claude Opus 4.7
 * Updated: 2026-04-24 (Claude Sonnet 4.6) - Phase 3: replaced Velocity with WAAPI via core-anim.esm.js
 * Updated: 2026-05-05 (Claude Opus 4.7) - Phase 11 cleanup: retired $.fn.expandableList shim
 *   and jQuery .trigger() (zero first-party consumers). Native CustomEvent + el.open()/close()
 *   replace the jQuery surface; E2E updated in lockstep.
 */

import { log } from './log.js';
import { slideDown, slideUp, slideRightIn, stop } from './core-anim.js';
import { getEasing } from './easing.js';

const OPENED_CLASS = 'expanded';
const INIT_CLASS = 'list-init';
const TOGGLE_SELECTOR = ':scope > .list-toggle-wrap > .js-list-toggle-button';
const CONTENTS_SELECTOR = '.js-list-contents';
const DURATION = 300;

class ExpandableList extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains(INIT_CLASS)) return;
    this.classList.add(INIT_CLASS);

    const toggle = this.querySelector(TOGGLE_SELECTOR);
    if (toggle) {
      toggle.addEventListener('click', this._handleToggle);
    }

    if (this.classList.contains(OPENED_CLASS)) {
      toggle && toggle.setAttribute('aria-expanded', 'true');
      const contents = this.querySelector(CONTENTS_SELECTOR);
      if (contents) {
        contents.style.display = 'block';
        contents.style.opacity = '1';
      }
    }
  }

  _handleToggle = (event) => {
    event.preventDefault();
    if (!this.classList.contains(OPENED_CLASS)) {
      this._closeOpenSiblings();
      this.open();
    } else {
      this.close();
      this._closeOpenChildren();
    }
  };

  _closeOpenSiblings() {
    const openSel = `expandable-list.${OPENED_CLASS}`;
    const targets = new Set();
    const parent = this.parentElement;
    if (parent) {
      parent.querySelectorAll(`:scope > ${openSel}`).forEach((el) => {
        if (el !== this) targets.add(el);
      });
      // Nested one level deep (mobile nav puts each list inside an <li>).
      const grandparent = parent.parentElement;
      if (grandparent) {
        grandparent.querySelectorAll(`:scope > * > ${openSel}`).forEach((el) => {
          if (el !== this) targets.add(el);
        });
      }
    }
    targets.forEach((el) => el.close());
  }

  _closeOpenChildren() {
    this.querySelectorAll(`expandable-list.${OPENED_CLASS}`).forEach((el) => el.close());
  }

  open() {
    const easing = getEasing();

    this.classList.add(OPENED_CLASS);
    this.dispatchEvent(new CustomEvent('list:open', { detail: true, bubbles: true }));
    const toggle = this.querySelector(TOGGLE_SELECTOR);
    toggle && toggle.setAttribute('aria-expanded', 'true');

    const contents = this.querySelector(CONTENTS_SELECTOR);
    if (!contents) return;

    slideDown(contents, { duration: DURATION, easing });
    slideRightIn(this.querySelectorAll('li'), { duration: DURATION, easing, stagger: 45 });
  }

  close() {
    const easing = getEasing();

    this.classList.remove(OPENED_CLASS);
    this.dispatchEvent(new CustomEvent('list:close', { detail: false, bubbles: true }));
    const toggle = this.querySelector(TOGGLE_SELECTOR);
    toggle && toggle.setAttribute('aria-expanded', 'false');

    const contents = this.querySelector(CONTENTS_SELECTOR);
    if (!contents) return;

    stop(contents);
    slideUp(contents, { duration: DURATION, easing });
  }
}

customElements.define('expandable-list', ExpandableList);
log('[ExpandableList] registered');

export { ExpandableList };
